import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { colors } from '../theme/modernTheme';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setLoading(true);

    try {
      await login({ email, password });
      navigate('/');
    } catch (err) {
      // Show error below both email and password fields
      setFieldErrors({
        email: 'Wrong Credentials. Please try again.',
        password: 'Wrong Credentials. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container component="main" sx={{ height: '100vh', overflow: 'hidden' }}>
      {/* Left Section - Branding */}
      <Grid
        item
        xs={false}
        sm={4}
        md={6}
        sx={{
          backgroundImage: `linear-gradient(135deg, ${colors.background.default} 0%, #E2E8F0 100%)`,
          display: isMobile ? 'none' : 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          position: 'relative',
        }}
      >
        <Fade in={true} timeout={1000}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              maxWidth: 480,
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="/logo.png"
              alt="TyagDMS Logo"
              sx={{
                height: 180,
                width: 'auto',
                mb: 6,
                mixBlendMode: 'multiply',
                objectFit: 'contain',
              }}
            />

            {/* Motivational Quote */}
            <Typography
              variant="h4"
              component="blockquote"
              sx={{
                color: colors.text.primary,
                fontWeight: 600,
                fontStyle: 'italic',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              "The best way to find yourself is to lose yourself in the service of others"
            </Typography>
          </Box>
        </Fade>
      </Grid>

      {/* Right Section - Login Form */}
      <Grid
        item
        xs={12}
        sm={8}
        md={6}
        component={Box}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Fade in={true} timeout={800}>
          <Box
            sx={{
              width: '100%',
              maxWidth: 500,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Mobile Logo (Visible only on small screens) */}
            {isMobile && (
              <Box
                component="img"
                src="/logo.png"
                alt="TyagDMS Logo"
                sx={{
                  height: 60,
                  width: 'auto',
                  mb: 4,
                  alignSelf: 'center',
                  mixBlendMode: 'multiply',
                }}
              />
            )}

            <Typography
              component="h1"
              variant="h4"
              sx={{
                mb: 1,
                fontWeight: 700,
                color: colors.text.primary,
                letterSpacing: '-0.02em',
              }}
            >
              Welcome To TyagDMS
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                color: colors.text.secondary,
              }}
            >
              Please enter your details to sign in.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontWeight: 600,
                  mb: 1,
                  display: 'block',
                }}
              >
                Email
              </Typography>
              <TextField
                required
                fullWidth
                id="email"
                placeholder="Enter your email"
                name="email"
                autoFocus
                size="small"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  // Clear errors when user starts typing
                  if (fieldErrors.email) {
                    setFieldErrors((prev) => ({ ...prev, email: null }));
                  }
                }}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                FormHelperTextProps={{ sx: { marginLeft: 0 } }}
                InputProps={{
                  sx: {
                    borderRadius: '4px',
                    fontSize: '0.95rem',
                    backgroundColor: colors.background.inputDefault,
                    border: '1px solid transparent',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused': {
                      backgroundColor: colors.background.inputDefault,
                      border: `1px solid ${colors.primary.main}`,
                    },
                  },
                }}
                sx={{ mb: 3 }}
              />

              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  fontWeight: 600,
                  mb: 1,
                  display: 'block',
                }}
              >
                Password
              </Typography>
              <TextField
                required
                fullWidth
                name="password"
                placeholder="Enter your password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                size="small"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  // Clear errors when user starts typing
                  if (fieldErrors.password) {
                    setFieldErrors((prev) => ({ ...prev, password: null }));
                  }
                }}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password}
                FormHelperTextProps={{ sx: { marginLeft: 0 } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: colors.text.secondary }}
                      >
                        {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '4px',
                    fontSize: '0.95rem',
                    backgroundColor: colors.background.inputDefault,
                    border: '1px solid transparent',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused': {
                      backgroundColor: colors.background.inputDefault,
                      border: `1px solid ${colors.primary.main}`,
                    },
                  },
                }}
                sx={{ mb: 4 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !email.trim() || !password.trim()}
                sx={{
                  py: 1.5,
                  height: 40,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '4px',
                  backgroundColor: colors.primary.main,
                  '&:hover': {
                    backgroundColor: colors.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: colors.text.disabled,
                    color: '#FFFFFF',
                    opacity: 0.6,
                  },
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              <Typography
                variant="body2"
                align="center"
                sx={{ mt: 4, color: colors.text.disabled }}
              >
                © {new Date().getFullYear()} TyagDMS
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Grid>
    </Grid>
  );
}
